// Served from recaptcha.net rather than google.com: google.com is unreachable from mainland China,
// which is the majority of /join traffic. The anchor iframe inherits this host, so it must stay in
// sync with the frame-src allowlist in ApplicationHelper#block_google_signin_iframe.
const RECAPTCHA_HOST = "https://www.recaptcha.net"
const SCRIPT_ID = "recaptcha-api"

// A blocked host usually hangs rather than erroring, so `onerror` alone never settles the promise.
const LOAD_TIMEOUT_MS = 8000

let loadPromise = null

// Exported for tests; callers should not need this.
export function resetRecaptchaLoader() {
  loadPromise = null
}

const whenReady = () => new Promise(resolve => grecaptcha.ready(() => resolve(grecaptcha)))

// Resolves with the `grecaptcha` global, or rejects if it can't be reached. Memoized, so warming
// early and awaiting again at submit reuses one load.
export default function loadRecaptcha(sitekey) {
  // Checked before the memo on purpose: an earlier attempt may have timed out while the script was
  // still in flight. If it has landed since, use it rather than replaying the stale failure and
  // submitting tokenless — the server rejects that, so a recovered load must still count.
  if (typeof grecaptcha !== "undefined") return whenReady()

  if (loadPromise) return loadPromise

  // A previous attempt may have left a failed or hung tag behind; don't stack a duplicate id on it.
  document.getElementById(SCRIPT_ID)?.remove()

  const ready = window.App.loadScriptAsync(
    `${RECAPTCHA_HOST}/recaptcha/api.js?render=${sitekey}`,
    SCRIPT_ID,
    null,
    1,
  ).then(() => {
    if (typeof grecaptcha === "undefined") {
      throw new Error("reCAPTCHA loaded without defining grecaptcha")
    }
    return whenReady()
  })

  const timeout = new Promise((_resolve, reject) => {
    setTimeout(() => reject(new Error("reCAPTCHA timed out")), LOAD_TIMEOUT_MS)
  })

  // Never memoize the failure. A transient error would otherwise poison the loader for the life of
  // the page, so every later submit would go tokenless and be rejected.
  loadPromise = Promise.race([ready, timeout]).catch(error => {
    loadPromise = null
    throw error
  })

  return loadPromise
}
